import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowRight, Quote, Mail, MapPin } from "lucide-react";
import { IMAGES, SUBBRANDS, WHAT_WE_DO, BRAND_CATEGORIES } from "../data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Overline = ({ children, testid }) => (
  <p data-testid={testid} className="text-xs md:text-sm uppercase tracking-[0.3em] text-wtei-primary font-medium mb-5">
    {children}
  </p>
);

export default function Home() {
  return (
    <main data-testid="home-page" className="bg-[#050505]">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Luxury travel destination" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-20">
          <motion.div initial="hidden" animate="visible" className="max-w-4xl">
            <motion.div variants={fadeUp} custom={0}>
              <Overline testid="hero-overline">World Travel Excellence Institute</Overline>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="font-heading text-5xl md:text-7xl lg:text-[5.5rem] font-light leading-[1.05] tracking-tight text-wtei-text text-balance"
            >
              Promoting excellence across the world of{" "}
              <span className="gold-gradient-text italic">travel & tourism</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="font-body text-lg md:text-xl text-wtei-secondary font-light leading-relaxed mt-8 max-w-2xl"
            >
              We enhance quality, strengthen professional competencies, encourage innovation, and support sustainable tourism through internationally recognized education, certification, research, and strategic partnerships.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-4 mt-12">
              <a
                data-testid="hero-brands-btn"
                href="#brands"
                className="group inline-flex items-center justify-center gap-2 bg-wtei-primary text-black px-9 py-4 uppercase tracking-widest text-sm font-medium hover:bg-wtei-primaryHover transition-all duration-300"
              >
                Explore Our Brands
                <ArrowRight strokeWidth={1.5} size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                data-testid="hero-about-btn"
                href="#about"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-wtei-text px-9 py-4 uppercase tracking-widest text-sm hover:border-wtei-primary hover:text-wtei-primary transition-all duration-300"
              >
                About the Institute
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section data-testid="mission-vision-section" className="relative py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20">
            {[
              { label: "Our Mission", text: "To advance excellence in global travel and tourism through education, quality certification, research, innovation, and international collaboration." },
              { label: "Our Vision", text: "To become the world's leading institute for travel excellence, recognized for setting benchmarks in professional development, quality assurance, and sustainable tourism." },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                data-testid={`mv-card-${i}`}
                className="relative border-l border-wtei-primary/40 pl-8 md:pl-10"
              >
                <Quote strokeWidth={1} className="text-wtei-primary mb-6" size={40} />
                <Overline>{item.label}</Overline>
                <p className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-light leading-snug text-wtei-text italic">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" id="about" className="relative py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
              <Overline testid="about-overline">About the Institute</Overline>
              <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight text-wtei-text mb-8">
                Committed to excellence in every sector of travel
              </h2>
              <div className="space-y-6 font-body text-base md:text-lg text-wtei-secondary font-light leading-relaxed">
                <p>
                  The World Travel Excellence Institute is committed to promoting excellence throughout every sector of the travel and tourism industry. We bring together education, rigorous quality standards, and global recognition to elevate the profession as a whole.
                </p>
                <p>
                  Through internationally recognized education, certification programs, research, and strategic partnerships, we enhance quality, strengthen professional competencies, encourage innovation, and support sustainable tourism worldwide.
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="relative"
            >
              <div className="border border-wtei-primary/60 p-3">
                <img src={IMAGES.about} alt="Six multicultural travel and tourism professionals collaborating around a table with laptops" className="w-full h-[520px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-wtei-primary text-black px-8 py-6 max-w-[220px] hidden md:block">
                <p className="font-heading text-2xl leading-tight">Setting the global benchmark</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section data-testid="what-we-do-section" id="what-we-do" className="relative py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mb-16">
            <Overline testid="wwd-overline">What We Do</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight text-wtei-text">
              A complete framework for travel excellence
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {WHAT_WE_DO.map((item, i) => {
              const Icon = Icons[item.icon] || Icons.Star;
              return (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  custom={i % 3}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  data-testid={`wwd-card-${i}`}
                  className="group bg-[#0a0a0a] p-10 hover:bg-[#111111] transition-colors duration-500"
                >
                  <Icon strokeWidth={1} size={36} className="text-wtei-primary mb-6 group-hover:scale-110 transition-transform duration-500 origin-left" />
                  <h3 className="font-heading text-2xl md:text-3xl text-wtei-text mb-3">{item.title}</h3>
                  <p className="font-body text-sm md:text-base text-wtei-secondary font-light leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section data-testid="brands-section" id="brands" className="relative py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mb-20">
            <Overline testid="brands-overline">Our Brands</Overline>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight text-wtei-text">
              Excellence across certification, awards & ratings
            </h2>
            <p className="font-body text-base md:text-lg text-wtei-secondary font-light leading-relaxed mt-5">
              Each WTEI brand advances a distinct dimension of quality, recognition, and prestige across the global travel landscape.
            </p>
          </motion.div>

          <div className="space-y-20">
            {BRAND_CATEGORIES.map((cat) => {
              const brands = SUBBRANDS.filter((b) => b.category === cat.name);
              return (
                <div key={cat.name} data-testid={`brand-category-${cat.name.toLowerCase()}`}>
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-8 pb-5 border-b border-white/10">
                    <h3 className="font-heading text-3xl md:text-4xl text-wtei-text">
                      {cat.name}
                    </h3>
                    <p className="font-body text-sm md:text-base text-wtei-secondary font-light max-w-md md:text-right">{cat.desc}</p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {brands.map((b, i) => (
                      <motion.div
                        key={b.id}
                        variants={fadeUp}
                        custom={i}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                        data-testid={`brand-card-${b.id}`}
                        className="group relative overflow-hidden border border-white/10 hover:border-wtei-primary/60 transition-all duration-500"
                      >
                        <div className="relative h-[420px] overflow-hidden">
                          <img src={b.image} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20 group-hover:from-black/90 transition-all duration-500" />
                        </div>
                        <div className="absolute inset-0 p-8 flex flex-col justify-end">
                          <p className="text-[10px] uppercase tracking-[0.3em] text-wtei-primary mb-3">{b.tagline}</p>
                          <h3 className="font-heading text-3xl text-wtei-text mb-3">{b.name}</h3>
                          <p className="font-body text-sm text-wtei-secondary font-light leading-relaxed mb-6 line-clamp-3">{b.short}</p>
                          <div className="flex items-center gap-4">
                            <a
                              data-testid={`brand-learn-${b.id}`}
                              href={b.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-wtei-text hover:text-wtei-primary transition-colors"
                            >
                              Learn More <ArrowUpRight strokeWidth={1.5} size={16} />
                            </a>
                            <a
                              data-testid={`brand-visit-${b.id}`}
                              href={b.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-wtei-secondary hover:text-wtei-primary transition-colors ml-auto"
                            >
                              {b.urlLabel} <ArrowUpRight strokeWidth={1.5} size={14} />
                            </a>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section data-testid="contact-section" id="contact" className="relative py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative border border-wtei-primary/30 bg-gradient-to-br from-[#111111] to-[#0a0a0a] p-10 md:p-20 text-center">
            <Overline testid="contact-overline">Get in Touch</Overline>
            <h2 className="font-heading text-4xl md:text-6xl font-light leading-tight text-wtei-text max-w-3xl mx-auto text-balance">
              Partner with the institute setting the standard for travel
            </h2>
            <p className="font-body text-base md:text-lg text-wtei-secondary font-light leading-relaxed mt-6 max-w-2xl mx-auto">
              For certification, awards, research, or strategic partnership inquiries, our team is ready to connect.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-12">
              <a
                data-testid="contact-email-btn"
                href="mailto:info@wtei.org"
                className="group inline-flex items-center gap-3 bg-wtei-primary text-black px-9 py-4 uppercase tracking-widest text-sm font-medium hover:bg-wtei-primaryHover transition-all duration-300"
              >
                <Mail strokeWidth={1.5} size={18} /> info@wtei.org
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
